import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getApiClientErrorStatus } from '@/services/api';
import { getDsBrandDocumentGenerateStatusQueryOptions } from '@/services/db/ds-brand-document/queries';
import { $brandWizardDraft, updateBrandWizardDraft, type BrandWizardDraft } from '../wizard/draft-store';
import {
  AUTHORING_POLL_INTERVAL_MS,
  deriveBrandRunState,
  phaseToPersistFor,
  terminalPhaseFor,
  type BrandRunState,
  type BrandRunStatusInput,
} from './brand-run-state';

type Tracker = {
  draft: BrandWizardDraft | null;
  state: BrandRunState | null;
  /** Polls once now and resolves with the state that result derives to. */
  refetch: () => Promise<BrandRunState | null>;
};

/**
 * Follows the workspace's in-flight brand authoring run from anywhere in the
 * app. Polls the SAME query key the wizard uses, so however many surfaces mount
 * (wizard takeover, Styles banner, layout snackbar) there is one network poll.
 * Keeps polling with the tab in the background so a run that finishes while the
 * user is elsewhere is already "ready" when they come back.
 *
 * The poll only runs while the draft says `authoring`, and every outcome —
 * including a stall — is written back to the draft, so a run that stops
 * answering stops being polled instead of ticking on from every page forever.
 */
export function useBrandRunTracker(workspaceId: number | null): Tracker {
  const stored = useStore($brandWizardDraft);
  const draft = stored != null && workspaceId != null && stored.workspaceId === workspaceId ? stored : null;
  const run = draft?.authorRun ?? null;
  const shouldPoll = draft?.phase === 'authoring' && run != null && workspaceId != null;

  const query = useQuery({
    ...getDsBrandDocumentGenerateStatusQueryOptions(workspaceId ?? 0, run ?? { workflowId: '', runId: '' }),
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? AUTHORING_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const errorStatus = query.isError ? (getApiClientErrorStatus(query.error) ?? null) : null;
  const status: BrandRunStatusInput = { data: query.data, isError: query.isError, errorStatus };
  const { data, isError, dataUpdatedAt, errorUpdatedAt } = query;
  const startedAt = draft?.startedAt ?? null;

  useEffect(
    // Reacts to poll results, so it must re-run per result — not mount-only.
    function recordPollOutcome() {
      if (!shouldPoll || startedAt == null) return;
      // The update stamps, not just the payload: an unchanged `in_progress`
      // body comes back with the same object identity but a newer stamp, and
      // the stall window has to be re-read against every result. Zero means
      // nothing has landed yet, so there is no outcome to record.
      const polledAt = Math.max(dataUpdatedAt, errorUpdatedAt);
      if (polledAt === 0) return;
      const phase = phaseToPersistFor({ data, isError, errorStatus }, startedAt, Date.now());
      if (phase != null) updateBrandWizardDraft({ phase });
    },
    [shouldPoll, startedAt, data, isError, errorStatus, dataUpdatedAt, errorUpdatedAt],
  );

  // Derived per render rather than memoized: the stall window is a comparison
  // against the current clock, which a memo keyed on payload identity would
  // freeze for as long as the server keeps repeating the same body.
  const state = draft == null ? null : deriveBrandRunState(draft, status, Date.now());

  /**
   * One manual status fetch, for "Check again" on a run that stopped answering.
   * TanStack still fetches a disabled query on an explicit refetch, which is
   * what lets a stalled run be re-checked without the background poll running.
   *
   * Resolves with the FRESH derivation rather than leaving the caller to read a
   * state it captured before the await, and persists it: the poll effect is off
   * while the draft is terminal, so this is the only writer for this result.
   */
  const refetch = async (): Promise<BrandRunState | null> => {
    const current = draft;
    if (current == null) return null;
    const result = await query.refetch();
    const fresh: BrandRunStatusInput = {
      data: result.data,
      isError: result.isError,
      errorStatus: result.isError ? (getApiClientErrorStatus(result.error) ?? null) : null,
    };
    // Still not answering: leave the draft exactly as it was, so a stalled run
    // stays stalled rather than being revived on no evidence. An errored refetch
    // keeps the last successful payload on `result.data`, so check the error first.
    if (result.isError || result.data == null) return deriveBrandRunState(current, fresh, Date.now());
    // The server just answered, so the stall clock is not part of this verdict:
    // an in-progress run is alive, and gets a fresh clock so the background
    // poll (and the wizard takeover) resume from now.
    const terminal = terminalPhaseFor(result.data);
    const phase = terminal ?? 'authoring';
    const startedAgainAt = terminal == null ? Date.now() : current.startedAt;
    updateBrandWizardDraft({ phase, startedAt: startedAgainAt });
    return deriveBrandRunState({ ...current, phase, startedAt: startedAgainAt }, fresh, startedAgainAt);
  };

  return { draft, state, refetch };
}
